import React from 'react';
import { connect } from 'react-redux';
import { Modal } from 'antd';
import { HIDE_MEASUREMENT_MODAL } from '../../ducks/Global/constants';
import MeasurementForm from '../../presentational/MeasurementForm';
import ManualForm from '../../presentational/MeasurementForm/ManualForm';
import moment from 'moment';
import { hasMinimumRole } from '../../utils/permissions';
import './index.css';

/**
 * Add Measurement Modal — V2 design.
 *
 * Shell (chrome + tabs) is local; the two forms it hosts (Manual /
 * 3-Site Skinfold) keep their existing redux wiring. Only the
 * presentation layer changed in the V2 pass:
 *   - Native modal chrome (theme tokens, hairline border, sticky
 *     header) replaces the antd default
 *   - Segmented tab pill replaces antd `<Tabs>`
 *   - Inputs are native + V2-styled inside each form
 *
 * Trainers see both tabs (Manual + 3-Site); clients only see Manual.
 * Non-pro clients see the upgrade gate instead of either form.
 */
class MeasurementModal extends React.Component {
  constructor() {
    super();
    this.state = { tab: 'manual' };
  }

  renderHeader = () => {
    const { data, isMetric, weightObj } = this.props;
    const isTrainerView =
      data && hasMinimumRole(this.props.role, 'trainer');

    if (isTrainerView) {
      // `data.currentWeight` is in the client's own `isMetric`
      // unit; when the viewer's display preference differs (trainer
      // pref vs client profile), convert for display so the label
      // and value always agree. The `isMetric` prop is the resolved
      // viewer unit, passed by whichever surface dispatched
      // SHOW_MEASUREMENT_MODAL.
      const profileIsMetric = !!data.isMetric;
      const stored = Number(data.currentWeight);
      let shown = stored;
      if (Number.isFinite(stored) && profileIsMetric !== isMetric) {
        shown = isMetric
          ? Math.round((stored / 2.205) * 10) / 10
          : Math.round(stored * 2.205 * 10) / 10;
      }
      return (
        <div className="mmv2-head">
          <div className="eyebrow mmv2-eyebrow">Add measurement</div>
          <h2 className="mmv2-title">
            {data.user.name} {data.user.lastName}
          </h2>
          <p className="muted mmv2-sub">
            Last {shown} {isMetric ? 'kg' : 'lb'}
          </p>
        </div>
      );
    }

    return (
      <div className="mmv2-head">
        <div className="eyebrow mmv2-eyebrow">New entry</div>
        <h2 className="mmv2-title">Add measurement</h2>
        {weightObj ? (
          <p className="muted mmv2-sub">
            Last updated {moment(weightObj.date).fromNow()}
          </p>
        ) : null}
      </div>
    );
  };

  renderTabs = () => {
    const { user } = this.props;
    const showThreeSite =
      user && hasMinimumRole(user.role, 'trainer');

    return (
      <>
        {showThreeSite ? (
          <div className="mmv2-seg">
            <button
              type="button"
              className={`mmv2-seg-tab ${
                this.state.tab === 'manual' ? 'is-active' : ''
              }`}
              onClick={() => this.setState({ tab: 'manual' })}
            >
              Manual
            </button>
            <button
              type="button"
              className={`mmv2-seg-tab ${
                this.state.tab === 'three-site' ? 'is-active' : ''
              }`}
              onClick={() => this.setState({ tab: 'three-site' })}
            >
              3-Site Skinfold
            </button>
          </div>
        ) : null}

        {this.state.tab === 'manual' ? (
          <ManualForm
            createOrUpdateTodo={this.props.createOrUpdateTodo}
            todo={this.props.todo}
            data={this.props.data}
            user={this.props.user}
            closeModal={this.props.closeModal}
          />
        ) : (
          <MeasurementForm
            data={this.props.data}
            user={this.props.user}
          />
        )}
      </>
    );
  };

  render() {
    // No isPro gate. This is a B2B coaching tool — the trainer pays, so
    // their clients (and the trainer logging on a client's behalf) always
    // get full measurement access. The previous gate read the LOGGED-IN
    // user's `isPro` (the trainer's when a trainer opened the modal),
    // which paywalled a core coaching action behind the trainer's own
    // subscription state. Removed in Wave 0 of the 2026-06-01 audit
    // (isPro paywall sweep). The 3-Site Skinfold tab is still gated by
    // role inside renderTabs(), which is the correct gate.
    return (
      <Modal
        className="measurement-modal-v2"
        visible={this.props.showModal}
        onCancel={this.props.closeModal}
        footer={null}
        centered
        width={640}
        bodyStyle={{ padding: 0 }}
        title={null}
        destroyOnClose
      >
        <div className="mmv2-shell">
          {this.renderHeader()}
          <div className="mmv2-body">{this.renderTabs()}</div>
        </div>
      </Modal>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    showModal: state.global.showMeasurementModal,
    user: state.auth.user,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    closeModal: () => {
      return dispatch({
        type: HIDE_MEASUREMENT_MODAL,
      });
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(MeasurementModal);
